import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faTimesCircle} from '@fortawesome/pro-solid-svg-icons/faTimesCircle';

import Base from 'Components/Base/Base';
import {Label, InputField, Button} from 'Components/Partials/Form';
import {PreAuth, Alert, Link} from 'Components/Partials/Layouts';

import Auth from 'Services/Api/Auth/Auth';

/**
 * @classdesc Login Page
 */
class Login extends Base {
    /**
     * @var state
     */
    state = {
        form: {
            email: '',
            password: '',
        },
        working: false,
        alert: null,
        loginStore: [],
        showForm: false,
        hideEmail: false,
    };

    /**
     * @method componentDidMount
     */
    componentDidMount = () => {
        this.loadLoginStore();

        Auth.on('login', this.storeLogin);
    };

    /**
     * @method componentWillUnmount
     */
    componentWillUnmount = () => {
        Auth.removeListener('login', this.storeLogin);
    };

    /**
     * @method loadLoginStore
     */
    loadLoginStore = () => {
        let loginStore = null;

        try {
            loginStore = localStorage.getItem('login_store');
            loginStore = loginStore !== null ? JSON.parse(loginStore) : [];
        } catch (e) {
            loginStore = [];
        }

        this.setState({
            loginStore,
        });
    };

    /**
     * @method storeLogin
     */
    storeLogin = (data) => {
        const {loginStore} = this.state;
        const {user} = data.data.data;
        const k = loginStore.map(s => s.email).indexOf(user.email);

        if (k === -1) {
            loginStore.push({
                id: user.id,
                email: user.email,
                name: `${user.first_name} ${user.last_name}`,
                avatar: user.avatar_url,
            });
        } else {
            loginStore[k] = {
                id: user.id,
                email: user.email,
                name: `${user.first_name} ${user.last_name}`,
                avatar: user.avatar_url,
            };
        }

        localStorage.setItem('login_store', JSON.stringify(loginStore));
    };

    /**
     * @method handleLogin
     * @param {Event} e
     * @return {Promise<void>}
     */
    handleLogin = async (e) => {
        e.preventDefault();
        this.working();

        const {form} = this.state;
        const login = await Auth.login(form.email, form.password);

        if (login.status !== 200) {
            this.formErrors('Sorry, your username and password could not be matched');
        }
    };

    /**
     * @method handleShowForm
     */
    handleShowForm = () => {
        this.setState({
            showForm: !this.state.showForm,
        });
    };

    /**
     * @method handleSetLogin
     * @param profile
     */
    handleSetLogin = (profile) => {
        this.setState({
            showForm: true,
            hideEmail: true,
            form: {
                ...this.state.form,
                email: profile.email,
            },
        });
    };

    /**
     * @method handleCancel
     */
    handleCancel = () => {
        this.setState({
            showForm: false,
            hideEmail: false,
            form: {
                email: '',
                password: '',
            },
        });
    };

    /**
     * @method handleDeleteLogin
     * @param {number} key
     */
    handleDeleteLogin = (key) => {
        const {loginStore} = this.state;

        loginStore.splice(key, 1);

        localStorage.setItem('login_store', JSON.stringify(loginStore));

        this.setState({
            loginStore,
        });
    };

    /**
     * @method render
     * @return {*}
     */
    render = () => {
        const {loginStore, showForm} = this.state;

        return (
            <PreAuth company={this.props.company}>
                <React.Fragment>
                    <h2
                        className="mt-6 text-3xl leading-9 font-extrabold text-gray-900">
                        Sign in to your account
                    </h2>

                    <div className="mt-8">
                        {(loginStore.length === 0 || showForm) && this.renderLoginForm()}
                        {(loginStore.length > 0 && !showForm) && this.renderProfiles()}
                    </div>
                </React.Fragment>
            </PreAuth>
        );
    };

    /**
     * @method renderProfiles
     */
    renderProfiles = () => {
        const {loginStore} = this.state;

        return (
            <React.Fragment>
                <div className="bg-white shadow sm:rounded-md">
                    <ul>
                        {loginStore.map((profile, k) => (
                            <li
                                className={`${k !== 0 ? 'border-t border-gray-200' : ''}`}
                                key={profile.id}
                            >
                                <div
                                    className="login-profile cursor-pointer block hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition duration-150 ease-in-out relative"
                                >
                                    <div
                                        className="absolute top-0 right-0 -mt-6 -mr-6 hidden close-btn p-4"
                                        onClick={() => this.handleDeleteLogin(k)}
                                    >
                                        <FontAwesomeIcon icon={faTimesCircle}
                                                         size="lg"/>
                                    </div>

                                    <div
                                        className="flex items-center px-4 py-4 sm:px-6"
                                        onClick={() => this.handleSetLogin(profile)}
                                    >
                                        <div
                                            className="min-w-0 flex-1 flex items-center">
                                            <div className="shrink-0">
                                                <img
                                                    className="h-12 w-12 rounded-full"
                                                    src={profile.avatar}
                                                    alt=""
                                                />
                                            </div>
                                            <div
                                                className="min-w-0 flex-1 px-4">
                                                <div>
                                                    <div
                                                        className="text-sm leading-5 font-medium text-app-blue-light truncate">{profile.name}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="relative mt-5">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300"></div>
                    </div>
                </div>

                <button className="
                      w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md bg-white text-sm leading-5
                      font-medium text-gray-500 hover:text-gray-400 focus:outline-none focus:border-blue-300
                      focus:shadow-outline-blue transition duration-150 ease-in-out mt-5
                    "
                        onClick={this.handleShowForm}
                >
                    Or enter your details
                </button>
            </React.Fragment>
        );
    };

    /**
     * @method renderLoginForm
     * @return {*}
     */
    renderLoginForm = () => {
        const {form, working, alert, hideEmail} = this.state;

        return (
            <div className="mt-6">
                <form onSubmit={this.handleLogin}>
                    {alert !== null && (<Alert {...alert} />)}

                    {!hideEmail && (
                        <div>
                            <Label label="Email" htmlFor="email"/>
                            <div className="mt-1 rounded-md shadow-sm">
                                <InputField
                                    type="email"
                                    id="email"
                                    value={form.email}
                                    onChange={(e) => {
                                        this.handleFormInput('email', e.target.value);
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    <div className="mt-6">
                        <Label label="Password" htmlFor="password"/>

                        <div className="mt-1 rounded-md shadow-sm">
                            <InputField
                                type="password"
                                id="password"
                                value={form.password}
                                onChange={(e) => {
                                    this.handleFormInput('password', e.target.value);
                                }}
                            />
                        </div>
                    </div>

                    <div className="mt-6 flex items-center justify-end">
                        <div className="text-sm leading-5">
                            <Link
                                to="/forgot-password"
                                label={'Forgot your password?\n'}
                            />
                        </div>
                    </div>

                    <div className="mt-6">
            <span className="block w-full">
              <Button
                  label="Sign In"
                  working={working}
              />

              <Button
                  className="ml-4"
                  label="Cancel"
                  onClick={this.handleCancel}
              />
            </span>
                    </div>
                </form>
            </div>
        );
    };
}

export default Login;
